import { Button, Card, Empty, Input, Select, Table, Tag } from "antd";
import { TableActionHeader } from "components/Global";
import { CalculatorOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { ChangeEvent, Key, useCallback, useEffect, useState } from "react";
import { getApi, postApi } from "redux/apis";
import { SALES_ORDER_STATUS } from "utils/shipment";
import { pageSizeOptionsPaging } from "utils/constant";
import { toastText } from "utils/utils";
import EstimateResultModal from "./EstimateResultModal";
import {
  formatShippingAddress,
  normalizeEstimateResult,
} from "./estimateUtils";
import {
  CustomerOption,
  EstimateResult,
  SalesOrderListItem,
} from "./types";
import styles from "./index.module.scss";
import "./index.scss";

const ShipmentEstimator = () => {
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [salesOrders, setSalesOrders] = useState<SalesOrderListItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateResult, setEstimateResult] = useState<EstimateResult | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await getApi("/sales-order/customerName");
        setCustomers(
          response.data.data.map((center: { id: string; name: string }) => ({
            value: center.id,
            label: center.name,
          })),
        );
      } catch {
        toastText("Failed to load customers", "error");
      }
    };
    fetchCustomers();
  }, []);

  const fetchSalesOrders = useCallback(async () => {
    if (!selectedCustomerId) {
      setSalesOrders([]);
      setTotalRecords(0);
      return;
    }

    setOrdersLoading(true);
    try {
      const response = await getApi("/sales-order/", {
        customerId: selectedCustomerId,
        searchValue: searchValue.trim(),
        page: currentPage,
        pageSize,
        sortBy: "shipDate",
        sortOrder: "desc",
      });
      setSalesOrders(response?.data?.data?.data ?? []);
      setTotalRecords(response?.data?.data?.total ?? 0);
    } catch {
      toastText("Failed to load sales orders", "error");
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedCustomerId, searchValue, currentPage, pageSize]);

  useEffect(() => {
    fetchSalesOrders();
  }, [fetchSalesOrders]);

  const handleCustomerChange = (customerId: string | null | undefined) => {
    setSelectedCustomerId(customerId ?? null);
    setSelectedRowKeys([]);
    setSearchValue("");
    setCurrentPage(1);
    setEstimateResult(null);
    setModalOpen(false);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setEstimateResult(null);
    setModalOpen(false);
  };

  const isRowSelectable = (record: SalesOrderListItem) =>
    record.status !== "Shipped" && record.status !== "UnderProcess";

  const toggleRowSelection = (orderId: string) => {
    const order = salesOrders.find((item) => item.id === orderId);
    if (!order || !isRowSelectable(order)) return;

    setSelectedRowKeys((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: !searchValue.trim(),
    onChange: (keys: Key[]) => {
      setSelectedRowKeys(keys as string[]);
    },
    getCheckboxProps: (record: SalesOrderListItem) => ({
      disabled: !isRowSelectable(record),
    }),
  };

  const handleEstimate = async () => {
    if (selectedRowKeys.length === 0) return;

    setEstimateLoading(true);
    try {
      const response = await postApi("/sales-order/estimate", {
        orderIds: selectedRowKeys,
      });
      const data = response?.data?.data;
      setEstimateResult(data ? normalizeEstimateResult(data) : null);
      setModalOpen(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Something went wrong while estimating shipment";
      toastText(message, "error");
    } finally {
      setEstimateLoading(false);
    }
  };

  const estimateButtonLabel =
    selectedRowKeys.length > 0
      ? `Estimate Shipment (${selectedRowKeys.length})`
      : "Estimate Shipment";

  const tableColumns = [
    {
      title: "SO #",
      dataIndex: "salesOrder",
      key: "salesOrder",
      render: (text: string) => <span className={styles.monoCell}>{text}</span>,
    },
    {
      title: "Date",
      dataIndex: "salesOrderDate",
      key: "salesOrderDate",
      render: (date: string) => moment(date).format("MM/DD/YYYY"),
    },
    {
      title: "Shipping Method",
      dataIndex: "shippingMethod",
      key: "shippingMethod",
      render: (text: string | null) => (text ? text : "-"),
    },
    {
      title: "Shipping Address",
      key: "shippingAddress",
      render: (_: unknown, record: SalesOrderListItem) =>
        formatShippingAddress(record.shippingAddress),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const statusConfig =
          SALES_ORDER_STATUS[status as keyof typeof SALES_ORDER_STATUS];
        const label = status.replace(/([a-z])([A-Z])/g, "$1 $2");
        return (
          <Tag color={statusConfig?.color ?? "default"}>{label}</Tag>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <TableActionHeader title="Shipment Estimator" />
      <p className={styles.subtitle}>
        Select a customer, choose open sales orders, and run a combined weight,
        volume, and pallet estimate.
      </p>

      <Card className={styles.customerCard}>
        <div className={styles.searchField}>
          <label className={styles.searchLabel} htmlFor="customer-select">
            CUSTOMER
          </label>
          <Select
            id="customer-select"
            placeholder="Select Customer"
            size="large"
            showSearch
            allowClear
            className={styles.customerSelect}
            value={selectedCustomerId}
            options={customers}
            onChange={handleCustomerChange}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </div>
      </Card>

      {!selectedCustomerId ? (
        <Card className={styles.emptyCard}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No customer selected — Pick a customer above to view their sales orders and run an estimate."
          />
        </Card>
      ) : (
        <Card className={styles.ordersCard}>
          <div className={styles.ordersHeader}>
            <Input
              size="large"
              placeholder="Search sales orders..."
              prefix={<SearchOutlined className={styles.searchIcon} />}
              value={searchValue}
              onChange={handleSearchChange}
              className={styles.orderSearchInput}
              allowClear
            />
            <Button
              type="primary"
              size="large"
              icon={<CalculatorOutlined />}
              loading={estimateLoading}
              disabled={selectedRowKeys.length === 0}
              onClick={handleEstimate}
              className={styles.estimateButton}
            >
              {estimateButtonLabel}
            </Button>
          </div>

          {selectedRowKeys.length === 0 && (
            <p className={styles.selectionHint}>
              Select one or more sales orders to run an estimate.
            </p>
          )}

          <Table
            dataSource={salesOrders}
            columns={tableColumns}
            rowKey="id"
            loading={ordersLoading}
            pagination={{
              total: totalRecords,
              current: currentPage,
              pageSize,
              pageSizeOptions: pageSizeOptionsPaging,
              showSizeChanger: true,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              onShowSizeChange: (_, size) => {
                setCurrentPage(1);
                setPageSize(size);
              },
            }}
            className="table-global"
            rowSelection={rowSelection}
            rowClassName={(record) =>
              selectedRowKeys.includes(record.id) ? styles.selectedRow : ""
            }
            onRow={(record) => ({
              onClick: () => toggleRowSelection(record.id),
              style: { cursor: isRowSelectable(record) ? "pointer" : "default" },
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No open sales orders found for this customer."
                />
              ),
            }}
          />
        </Card>
      )}

      <EstimateResultModal
        open={modalOpen}
        result={estimateResult}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default ShipmentEstimator;
